import React from 'react'
import {
  Box,
  Button,
  Divider,
  List,
  Popover,
  type PopoverOrigin,
  Skeleton,
  Typography,
} from '@mui/material'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'

import type { AppNotification } from '../../types/notifications'
import NotificationItem from './NotificationItem'

const TEAL = '#007f98'
const CHARCOAL = '#333333'

export interface NotificationAnchorConfig {
  anchorOrigin: PopoverOrigin
  transformOrigin: PopoverOrigin
}

interface NotificationMenuProps {
  open: boolean
  anchorEl: HTMLElement | null
  onClose: () => void
  notifications: AppNotification[]
  unread: number
  loading: boolean
  error: boolean
  onMarkSeen: (id: number) => void
  onMarkAllSeen: () => void
  onRetry: () => void
  /** Open the full history drawer (closes this popover). */
  onViewAll: () => void
  anchorConfig: NotificationAnchorConfig
  /** One-line account row, shown only on the admin condensed rail. */
  accountHeader?: React.ReactNode
}

/**
 * The notification dropdown: header + (optional account header) + the list,
 * with explicit loading / error / empty branches. Placement-agnostic - the
 * shell decides the anchor origins via `anchorConfig`.
 */
const NotificationMenu: React.FC<NotificationMenuProps> = ({
  open,
  anchorEl,
  onClose,
  notifications,
  unread,
  loading,
  error,
  onMarkSeen,
  onMarkAllSeen,
  onRetry,
  onViewAll,
  anchorConfig,
  accountHeader,
}) => {
  const showSkeletons = loading && notifications.length === 0
  const showError = error && !loading && notifications.length === 0
  const showEmpty = !loading && !error && notifications.length === 0

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={anchorConfig.anchorOrigin}
      transformOrigin={anchorConfig.transformOrigin}
      marginThreshold={8}
      slotProps={{
        paper: {
          sx: {
            width: 360,
            maxWidth: '95vw',
            maxHeight: 480,
            display: 'flex',
            flexDirection: 'column',
            borderRadius: '8px',
            boxShadow: '0 4px 16px rgba(0, 0, 0, 0.12)',
          },
        },
      }}
    >
      {accountHeader && (
        <>
          {accountHeader}
          <Divider />
        </>
      )}

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 2,
          py: 1,
        }}
      >
        <Typography sx={{ fontSize: 14, fontWeight: 600, color: CHARCOAL }}>
          Notifications
        </Typography>
        {unread > 0 && (
          <Button
            size="small"
            onClick={() => onMarkAllSeen()}
            sx={{ color: TEAL, textTransform: 'none' }}
          >
            Mark all read
          </Button>
        )}
      </Box>
      <Divider />

      {showSkeletons ? (
        <Box sx={{ px: 2, py: 1 }}>
          {[0, 1, 2].map((i) => (
            <Skeleton
              key={i}
              variant="rectangular"
              height={40}
              sx={{ borderRadius: 1, my: 1 }}
            />
          ))}
        </Box>
      ) : showError ? (
        <Box sx={{ px: 2, py: 4, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Couldn't load notifications.
          </Typography>
          <Button
            size="small"
            onClick={() => onRetry()}
            sx={{ color: TEAL, textTransform: 'none' }}
          >
            Retry
          </Button>
        </Box>
      ) : showEmpty ? (
        <Box sx={{ px: 2, py: 4, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            You're all caught up.
          </Typography>
        </Box>
      ) : (
        <List
          dense
          disablePadding
          sx={{ flex: 1, overflowY: 'auto' }}
          onClick={(e) => {
            // A row with a deep link closes the dropdown as it navigates.
            if ((e.target as HTMLElement).closest('a')) onClose()
          }}
        >
          {notifications.map((n) => (
            <NotificationItem key={n.id} notification={n} onActivate={onMarkSeen} />
          ))}
        </List>
      )}

      <Divider />
      <Box sx={{ px: 1, py: 0.5 }}>
        <Button
          fullWidth
          size="small"
          onClick={() => onViewAll()}
          endIcon={<ArrowForwardIcon fontSize="small" />}
          sx={{ color: TEAL, textTransform: 'none' }}
        >
          View all notifications
        </Button>
      </Box>
    </Popover>
  )
}

export default NotificationMenu
